import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Container,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import { alpha } from '@mui/material/styles';
import { DISPLAY_FONT, tokens } from '../theme';
import { salonApi, Salon, SalonService, PublicStaffMember } from '../services/salonService';
import { bookingApi } from '../services/bookingService';
import { paymentApi } from '../services/paymentService';
import { EmptyState, FieldGrid, LoadingState, PersonAvatar, SectionCard } from '../components/ui';
import { formatDuration, formatMoney, formatWeekdayDay, initials } from '../utils/format';

const steps = ['Usługa', 'Specjalista', 'Termin', 'Twoje dane'];

const STEP_HINTS = [
  'Wybierz usługę, na którą chcesz się umówić.',
  'Wybierz osobę, która wykona usługę.',
  'Wybierz dzień i godzinę wizyty.',
  'Sprawdź szczegóły wizyty i podaj swoje dane.',
];

const DAY_STRIP_LENGTH = 14;
const fWeekdayShort = new Intl.DateTimeFormat('pl-PL', { weekday: 'short' });
const fMonthShort = new Intl.DateTimeFormat('pl-PL', { month: 'short' });

const pad = (n: number) => String(n).padStart(2, '0');
const toIsoDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
// Data „RRRR-MM-DD” w strefie lokalnej (new Date('RRRR-MM-DD') liczy od polnocy UTC).
const parseLocalDate = (value: string) => {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const SLOT_PARTS = [
  { label: 'Przed południem', test: (h: number) => h < 12 },
  { label: 'Po południu', test: (h: number) => h >= 12 && h < 17 },
  { label: 'Wieczorem', test: (h: number) => h >= 17 },
];

function groupServicesByCategory(services: SalonService[]) {
  const groups = new Map<string, SalonService[]>();
  for (const service of services) {
    const key = service.category || 'Inne usługi';
    const list = groups.get(key) || [];
    list.push(service);
    groups.set(key, list);
  }
  return Array.from(groups.entries());
}

const clamp2 = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' } as const;

/** Klikalna karta wyboru z wyraznym stanem zaznaczenia. */
const SelectCard: React.FC<{ selected: boolean; onClick: () => void; children: React.ReactNode }> = ({ selected, onClick, children }) => (
  <ButtonBase
    onClick={onClick}
    aria-pressed={selected}
    sx={{
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 2,
      p: { xs: 1.75, sm: 2 },
      textAlign: 'left',
      borderRadius: '14px',
      bgcolor: selected ? tokens.plumSofter : tokens.paper,
      border: `1px solid ${selected ? tokens.plum : tokens.line}`,
      boxShadow: selected ? `inset 0 0 0 1px ${tokens.plum}` : 'none',
      transition: 'border-color .15s ease, background-color .15s ease',
      '&:hover': { borderColor: selected ? tokens.plum : tokens.lineStrong, bgcolor: selected ? tokens.plumSofter : tokens.surface },
      '&.Mui-focusVisible': { outline: `2px solid ${tokens.plum}`, outlineOffset: 2 },
    }}
  >
    {children}
    <Box
      aria-hidden
      sx={{
        width: 22,
        height: 22,
        flexShrink: 0,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        border: `1.5px solid ${selected ? tokens.plum : tokens.lineStrong}`,
        bgcolor: selected ? tokens.plum : 'transparent',
        color: tokens.paper,
      }}
    >
      {selected && <CheckRoundedIcon sx={{ fontSize: 16 }} />}
    </Box>
  </ButtonBase>
);

/** Pasek krokow: segmenty postepu, na telefonie z opisem „Krok 2 z 4”. */
const StepBar: React.FC<{ active: number }> = ({ active }) => (
  <Box sx={{ mb: { xs: 2.5, sm: 3 } }}>
    <Typography sx={{ display: { xs: 'block', sm: 'none' }, fontSize: '0.8125rem', fontWeight: 600, color: tokens.muted, mb: 1 }}>
      Krok {active + 1} z {steps.length} ·{' '}
      <Box component="span" sx={{ color: tokens.ink, fontWeight: 700 }}>{steps[active]}</Box>
    </Typography>
    <Box
      component="ol"
      aria-label="Kroki rezerwacji"
      sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`, gap: { xs: 0.75, sm: 1.5 } }}
    >
      {steps.map((label, i) => (
        <Box component="li" key={label} aria-current={i === active ? 'step' : undefined} sx={{ minWidth: 0 }}>
          <Box sx={{ height: 4, borderRadius: 99, bgcolor: i <= active ? tokens.plum : tokens.lineStrong, transition: 'background-color .2s ease' }} />
          <Typography
            noWrap
            sx={{
              display: { xs: 'none', sm: 'block' },
              mt: 1,
              fontSize: '0.8125rem',
              fontWeight: i === active ? 700 : 600,
              color: i === active ? tokens.ink : i < active ? tokens.inkSoft : tokens.subtle,
            }}
          >
            {i + 1}. {label}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

/** Przyciski krokow; na telefonie przyklejone do dolu ekranu. */
const ActionBar: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      position: { xs: 'sticky', sm: 'static' },
      bottom: 0,
      zIndex: 5,
      mt: 3,
      mx: { xs: -2, sm: 0 },
      px: { xs: 2, sm: 0 },
      pt: { xs: 1.5, sm: 0 },
      pb: { xs: 'calc(12px + env(safe-area-inset-bottom))', sm: 0 },
      bgcolor: { xs: alpha(tokens.paper, 0.96), sm: 'transparent' },
      backdropFilter: { xs: 'blur(10px)', sm: 'none' },
      borderTop: { xs: `1px solid ${tokens.line}`, sm: 'none' },
    }}
  >
    <Stack direction="row" gap={1.5} alignItems="center" justifyContent="space-between">
      {children}
    </Stack>
  </Box>
);

const SummaryRow: React.FC<{ label: string; children: React.ReactNode; onChange?: () => void; strong?: boolean }> = ({ label, children, onChange, strong }) => (
  <Stack
    direction="row"
    gap={{ xs: 1, sm: 2 }}
    alignItems="center"
    sx={{ py: 1.25, '&:not(:last-of-type)': { borderBottom: `1px solid ${tokens.line}` } }}
  >
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, gap: { xs: 0.25, sm: 2 } }}>
      <Typography sx={{ width: { sm: 120 }, flexShrink: 0, fontSize: '0.8125rem', color: tokens.muted }}>{label}</Typography>
      <Box sx={{ minWidth: 0, fontSize: strong ? '1.0625rem' : '0.9375rem', fontWeight: strong ? 750 : 600, fontVariantNumeric: 'tabular-nums' }}>{children}</Box>
    </Box>
    {onChange && (
      <Button variant="text" size="small" onClick={onChange} sx={{ flexShrink: 0, mr: -1 }}>
        Zmień
      </Button>
    )}
  </Stack>
);

const BookingHeader: React.FC<{ salon: Salon | null; slug?: string }> = ({ salon, slug }) => {
  const name = salon?.publicTitle || salon?.name || '';
  const words = name.split(' ');
  return (
    <Box component="header" sx={{ bgcolor: tokens.paper, borderBottom: `1px solid ${tokens.line}` }}>
      <Container maxWidth="md">
        <Stack direction="row" alignItems="center" gap={1.5} sx={{ minHeight: 64, py: 1 }}>
          <IconButton component={Link} to={`/s/${slug}`} edge="start" aria-label="Wróć do wizytówki salonu">
            <ArrowBackIcon />
          </IconButton>
          {salon ? (
            <>
              <Avatar
                variant="rounded"
                src={salon.logoUrl || undefined}
                alt=""
                sx={{ width: 40, height: 40, borderRadius: '11px', bgcolor: tokens.aubergine, color: tokens.champagne, fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: '0.9375rem' }}
              >
                {initials(words[0], words[1], name)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.9375rem', lineHeight: 1.35 }}>{name}</Typography>
                <Typography noWrap variant="caption" component="div">{salon.publicAddress || salon.city}</Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ flex: 1 }}>
              <Skeleton width={180} height={20} />
              <Skeleton width={120} height={16} />
            </Box>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export const PublicBookingPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [createdBookingId, setCreatedBookingId] = useState<string | null>(null);
  const [payLoading, setPayLoading] = useState(false);
  const [payError, setPayError] = useState('');
  const [salon, setSalon] = useState<Salon | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [selectedService, setSelectedService] = useState<SalonService | null>(null);
  const [selectedStaff, setSelectedStaff] = useState<PublicStaffMember | null>(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [notes, setNotes] = useState('');
  const [guest, setGuest] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    const load = async () => {
      if (!slug) {
        setError('Brak adresu salonu.');
        setLoading(false);
        return;
      }

      try {
        const data = await salonApi.getPublicBySlug(slug);
        if (!data.allowOnlineBooking) {
          setError('Ten salon nie przyjmuje rezerwacji online.');
          return;
        }
        setSalon(data);
      } catch {
        setError('Nie udało się załadować danych salonu.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  const staffList = useMemo(() => {
    const raw = (salon?.staff || []) as PublicStaffMember[];
    return raw.filter((member) => member.services && member.services.length > 0);
  }, [salon?.staff]);

  useEffect(() => {
    if (!salon || !selectedService || !selectedStaff || !selectedDate) return;

    const loadSlots = async () => {
      setSlotsLoading(true);
      setSelectedTime('');
      try {
        const slots = await bookingApi.getAvailableSlots({
          salonId: salon.id,
          staffId: selectedStaff.id,
          serviceId: selectedService.id,
          date: selectedDate,
        });
        setAvailableSlots(slots);
      } catch {
        setAvailableSlots([]);
      } finally {
        setSlotsLoading(false);
      }
    };
    loadSlots();
  }, [salon, selectedService, selectedStaff, selectedDate]);

  const minDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  }, []);

  const handleBook = async () => {
    if (!salon || !selectedService || !selectedStaff || !selectedDate || !selectedTime) return;

    setSubmitting(true);
    setError('');
    try {
      const booking = await bookingApi.createPublic({
        salonId: salon.id,
        serviceId: selectedService.id,
        staffId: selectedStaff.id,
        date: selectedDate,
        startTime: selectedTime,
        notes: notes || undefined,
        guest: {
          firstName: guest.firstName.trim(),
          lastName: guest.lastName.trim(),
          email: guest.email.trim(),
          phone: guest.phone.trim() || undefined,
        },
      });
      setCreatedBookingId(booking.id);
      setSuccess(true);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Błąd podczas rezerwacji.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePay = async () => {
    if (!createdBookingId) return;
    setPayLoading(true);
    setPayError('');
    try {
      const { url } = await paymentApi.bookingCheckout(createdBookingId);
      window.location.href = url;
    } catch (e: any) {
      setPayError(e?.response?.data?.message || 'Nie udało się rozpocząć płatności.');
      setPayLoading(false);
    }
  };

  const serviceGroups = useMemo(() => groupServicesByCategory(salon?.services || []), [salon?.services]);

  const dayStrip = useMemo(() => {
    const start = parseLocalDate(minDate);
    return Array.from({ length: DAY_STRIP_LENGTH }, (_, i) => {
      const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
      const hours = salon?.workingHours?.find((h) => h.dayOfWeek === d.getDay());
      return { iso: toIsoDate(d), date: d, closed: !!hours?.isClosed };
    });
  }, [minDate, salon?.workingHours]);

  const slotGroups = useMemo(
    () =>
      SLOT_PARTS.map((part) => ({
        label: part.label,
        slots: availableSlots.filter((slot) => part.test(Number(slot.split(':')[0]))),
      })).filter((g) => g.slots.length > 0),
    [availableSlots],
  );

  const termLabel = selectedDate && selectedTime ? `${formatWeekdayDay(parseLocalDate(selectedDate))}, godz. ${selectedTime}` : '';
  const salonName = salon?.publicTitle || salon?.name || '';

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: tokens.ground }}>
        <BookingHeader salon={null} slug={slug} />
        <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }} aria-busy="true" aria-label="Ładowanie rezerwacji">
          <Skeleton width={260} height={44} />
          <Skeleton width={320} height={22} sx={{ mb: 3 }} />
          <LoadingState variant="table" rows={4} />
        </Container>
      </Box>
    );
  }

  if (error && !salon) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: tokens.ground }}>
        <BookingHeader salon={null} slug={slug} />
        <Container maxWidth="sm" sx={{ py: { xs: 4, md: 8 } }}>
          <Paper>
            <EmptyState
              icon={<EventBusyOutlinedIcon />}
              title="Rezerwacja online jest niedostępna"
              description={`${error} Skontaktuj się z salonem bezpośrednio.`}
              actions={
                slug ? (
                  <Button component={Link} to={`/s/${slug}`} variant="outlined" startIcon={<ArrowBackIcon />}>
                    Wróć do wizytówki
                  </Button>
                ) : undefined
              }
            />
          </Paper>
        </Container>
      </Box>
    );
  }

  if (success && salon) {
    const canPay = !!(salon.paymentsEnabled && createdBookingId);
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: tokens.ground }}>
        <BookingHeader salon={salon} slug={slug} />
        <Container maxWidth="sm" sx={{ py: { xs: 3, md: 6 } }}>
          <Paper sx={{ p: { xs: 2.5, sm: 4 } }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: tokens.successSoft, color: tokens.success, display: 'grid', placeItems: 'center', mb: 2.5 }}>
              <CheckCircleOutlineRoundedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography variant="h1">Rezerwacja złożona</Typography>
            <Typography sx={{ mt: 1, color: tokens.inkSoft }}>
              Dziękujemy{guest.firstName.trim() ? `, ${guest.firstName.trim()}` : ''}. Oto szczegóły Twojej wizyty.
            </Typography>

            <Box sx={{ mt: 3, px: 2, py: 0.5, bgcolor: tokens.surface, borderRadius: '12px', border: `1px solid ${tokens.line}` }}>
              <SummaryRow label="Salon">{salonName}</SummaryRow>
              <SummaryRow label="Usługa">{selectedService?.name}</SummaryRow>
              {selectedStaff && <SummaryRow label="Specjalista">{selectedStaff.firstName} {selectedStaff.lastName}</SummaryRow>}
              <SummaryRow label="Termin">
                <Box component="span" sx={{ display: 'inline-block', '&::first-letter': { textTransform: 'uppercase' } }}>{termLabel || `${selectedDate} o ${selectedTime}`}</Box>
              </SummaryRow>
              {selectedService && <SummaryRow label="Cena">{formatMoney(selectedService.price)}</SummaryRow>}
            </Box>

            <Stack gap={1.25} sx={{ mt: 3 }}>
              {canPay && (
                <>
                  {payError && <Alert severity="error">{payError}</Alert>}
                  <Button variant="contained" size="large" fullWidth onClick={handlePay} disabled={payLoading}>
                    {payLoading
                      ? 'Przekierowanie…'
                      : `Zapłać online${selectedService ? ` · ${formatMoney(selectedService.price)}` : ''}`}
                  </Button>
                  <Typography variant="caption" component="div" sx={{ textAlign: 'center', mb: 0.5 }}>
                    Bezpieczna płatność kartą lub BLIK przez Stripe.
                  </Typography>
                </>
              )}
              <Button component={Link} to={`/s/${slug}`} size="large" fullWidth variant={canPay ? 'outlined' : 'contained'}>
                Wróć do wizytówki
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  const staffForService = selectedService
    ? staffList.filter((member) => member.services?.some((s) => s.service.id === selectedService.id))
    : [];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: tokens.ground }}>
      <BookingHeader salon={salon} slug={slug} />

      <Container maxWidth="md" component="main" sx={{ pt: { xs: 3, md: 5 }, pb: { xs: 0, sm: 6 } }}>
        <Box sx={{ mb: { xs: 2.5, sm: 3 } }}>
          <Typography variant="h1">Rezerwacja wizyty</Typography>
          <Typography sx={{ mt: 0.75, color: tokens.inkSoft, fontSize: '0.9375rem' }}>{STEP_HINTS[activeStep]}</Typography>
        </Box>

        <StepBar active={activeStep} />

        {(activeStep === 1 || activeStep === 2) && selectedService && (
          <Paper sx={{ px: 2, py: 1.5, mb: 2.5, bgcolor: tokens.surface }}>
            <Stack direction="row" gap={2} alignItems="center" justifyContent="space-between">
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontWeight: 650, fontSize: '0.9375rem' }}>{selectedService.name}</Typography>
                <Typography variant="caption" component="div" noWrap>
                  {formatDuration(selectedService.duration)}
                  {activeStep === 2 && selectedStaff ? ` · ${selectedStaff.firstName} ${selectedStaff.lastName}` : ''}
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {formatMoney(selectedService.price)}
              </Typography>
            </Stack>
          </Paper>
        )}

        {activeStep === 0 && (
          <Box sx={{ pb: { xs: 4, sm: 0 } }}>
            {serviceGroups.length > 0 ? (
              <Stack gap={3}>
                {serviceGroups.map(([category, items]) => (
                  <Box key={category} component="section">
                    <Typography component="h2" sx={{ fontSize: '0.8125rem', fontWeight: 700, color: tokens.muted, mb: 1.25 }}>
                      {category}
                    </Typography>
                    <Stack gap={1.25}>
                      {items.map((service) => {
                        const desc = service.publicDescription || service.description;
                        return (
                          <SelectCard
                            key={service.id}
                            selected={selectedService?.id === service.id}
                            onClick={() => {
                              setSelectedService(service);
                              setSelectedStaff(null);
                              setActiveStep(1);
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 650, fontSize: '1rem', lineHeight: 1.4 }}>{service.name}</Typography>
                              {desc && <Typography sx={{ mt: 0.25, color: tokens.inkSoft, fontSize: '0.875rem', ...clamp2 }}>{desc}</Typography>}
                            </Box>
                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: '1rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                {formatMoney(service.price)}
                              </Typography>
                              <Typography variant="caption" component="div" sx={{ whiteSpace: 'nowrap' }}>
                                {formatDuration(service.duration)}
                              </Typography>
                            </Box>
                          </SelectCard>
                        );
                      })}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Paper>
                <EmptyState
                  icon={<ContentCutOutlinedIcon />}
                  title="Brak usług dostępnych do rezerwacji online"
                  description="Skontaktuj się z salonem telefonicznie, aby umówić wizytę."
                  actions={<Button component={Link} to={`/s/${slug}`} variant="outlined">Wróć do wizytówki</Button>}
                />
              </Paper>
            )}
          </Box>
        )}

        {activeStep === 1 && selectedService && (
          <Box sx={{ pb: { xs: 4, sm: 0 } }}>
            {staffForService.length > 0 ? (
              <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
                {staffForService.map((member) => (
                  <SelectCard
                    key={member.id}
                    selected={selectedStaff?.id === member.id}
                    onClick={() => {
                      setSelectedStaff(member);
                      setActiveStep(2);
                    }}
                  >
                    <PersonAvatar firstName={member.firstName} lastName={member.lastName} seed={member.id} size={44} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 650, fontSize: '1rem', lineHeight: 1.4 }}>
                        {member.firstName} {member.lastName}
                      </Typography>
                      {member.specialization && (
                        <Typography variant="caption" component="div" sx={{ color: tokens.inkSoft }}>{member.specialization}</Typography>
                      )}
                      {member.bio && (
                        <Typography sx={{ mt: 0.5, color: tokens.muted, fontSize: '0.8125rem', ...clamp2 }}>{member.bio}</Typography>
                      )}
                    </Box>
                  </SelectCard>
                ))}
              </Box>
            ) : (
              <Paper>
                <EmptyState
                  compact
                  icon={<PersonSearchOutlinedIcon />}
                  title="Brak pracowników przypisanych do tej usługi"
                  description="Wybierz inną usługę albo skontaktuj się z salonem."
                />
              </Paper>
            )}
            <Button variant="text" startIcon={<ArrowBackIcon />} onClick={() => setActiveStep(0)} sx={{ mt: 2, ml: -1.5 }}>
              Wstecz
            </Button>
          </Box>
        )}

        {activeStep === 2 && selectedService && selectedStaff && (
          <Box>
            <Stack gap={2}>
              <SectionCard title="Dzień wizyty">
                <Box
                  role="group"
                  aria-label="Najbliższe dni"
                  sx={{
                    display: 'flex',
                    gap: 1,
                    overflowX: 'auto',
                    pb: 1,
                    mx: { xs: -2, md: -2.5 },
                    px: { xs: 2, md: 2.5 },
                    scrollSnapType: 'x proximity',
                    scrollbarWidth: 'thin',
                  }}
                >
                  {dayStrip.map((day) => {
                    const selected = selectedDate === day.iso;
                    return (
                      <ButtonBase
                        key={day.iso}
                        onClick={() => setSelectedDate(day.iso)}
                        aria-pressed={selected}
                        aria-label={`${formatWeekdayDay(day.date)}${day.closed ? ' (salon zwykle zamknięty)' : ''}`}
                        sx={{
                          flexShrink: 0,
                          width: 64,
                          py: 1.25,
                          borderRadius: '12px',
                          flexDirection: 'column',
                          gap: 0.25,
                          scrollSnapAlign: 'start',
                          border: `1px solid ${selected ? tokens.plum : tokens.lineStrong}`,
                          bgcolor: selected ? tokens.plum : day.closed ? tokens.surface : tokens.paper,
                          color: selected ? tokens.paper : day.closed ? tokens.subtle : tokens.ink,
                          transition: 'background-color .15s ease, border-color .15s ease',
                          '&:hover': { borderColor: tokens.plum, bgcolor: selected ? tokens.plumDark : tokens.plumSofter },
                          '&.Mui-focusVisible': { outline: `2px solid ${tokens.plum}`, outlineOffset: 2 },
                        }}
                      >
                        <Box component="span" sx={{ fontSize: '0.75rem', fontWeight: 600, color: selected ? alpha(tokens.paper, 0.8) : 'inherit' }}>
                          {fWeekdayShort.format(day.date).replace('.', '')}
                        </Box>
                        <Box component="span" sx={{ fontSize: '1.125rem', fontWeight: 750, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                          {day.date.getDate()}
                        </Box>
                        <Box component="span" sx={{ fontSize: '0.75rem', fontWeight: 600, color: selected ? alpha(tokens.paper, 0.8) : day.closed ? tokens.subtle : tokens.muted }}>
                          {fMonthShort.format(day.date).replace('.', '')}
                        </Box>
                      </ButtonBase>
                    );
                  })}
                </Box>
                <TextField
                  type="date"
                  label="Inny dzień"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  inputProps={{ min: minDate }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mt: 2, width: { xs: '100%', sm: 240 }, minWidth: 170 }}
                />
              </SectionCard>

              <SectionCard title="Godzina" subtitle={selectedDate ? formatWeekdayDay(parseLocalDate(selectedDate)) : undefined}>
                {!selectedDate && (
                  <Typography sx={{ color: tokens.muted, fontSize: '0.9375rem' }}>Wybierz dzień, aby zobaczyć wolne godziny.</Typography>
                )}

                {selectedDate && slotsLoading && (
                  <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))' }} aria-busy="true" aria-label="Ładowanie wolnych godzin">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <Skeleton key={i} variant="rounded" height={44} />
                    ))}
                  </Box>
                )}

                {selectedDate && !slotsLoading && availableSlots.length > 0 && (
                  <Stack gap={2}>
                    {slotGroups.map((group) => (
                      <Box key={group.label}>
                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: tokens.muted, mb: 1 }}>{group.label}</Typography>
                        <Box role="group" aria-label={group.label} sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))' }}>
                          {group.slots.map((slot) => {
                            const selected = selectedTime === slot;
                            return (
                              <ButtonBase
                                key={slot}
                                onClick={() => setSelectedTime(slot)}
                                aria-pressed={selected}
                                sx={{
                                  height: 44,
                                  borderRadius: '10px',
                                  fontSize: '0.9375rem',
                                  fontWeight: 650,
                                  fontFamily: 'inherit',
                                  fontVariantNumeric: 'tabular-nums',
                                  border: `1px solid ${selected ? tokens.plum : tokens.lineStrong}`,
                                  bgcolor: selected ? tokens.plum : tokens.paper,
                                  color: selected ? tokens.paper : tokens.ink,
                                  transition: 'background-color .15s ease, border-color .15s ease',
                                  '&:hover': { borderColor: tokens.plum, bgcolor: selected ? tokens.plumDark : tokens.plumSofter },
                                  '&.Mui-focusVisible': { outline: `2px solid ${tokens.plum}`, outlineOffset: 2 },
                                }}
                              >
                                {slot}
                              </ButtonBase>
                            );
                          })}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}

                {selectedDate && !slotsLoading && availableSlots.length === 0 && (
                  <Alert severity="warning">Brak dostępnych terminów w wybranym dniu. Wybierz inny dzień.</Alert>
                )}
              </SectionCard>
            </Stack>

            <ActionBar>
              <Button variant="text" onClick={() => setActiveStep(1)} sx={{ ml: { sm: -1.5 } }}>
                Wstecz
              </Button>
              <Button variant="contained" size="large" disabled={!selectedTime} onClick={() => setActiveStep(3)} sx={{ flex: { xs: 1, sm: 'none' }, minWidth: { sm: 200 } }}>
                {selectedTime ? `Dalej · ${selectedTime}` : 'Dalej'}
              </Button>
            </ActionBar>
          </Box>
        )}

        {activeStep === 3 && selectedService && selectedStaff && selectedTime && (
          <Box>
            <Stack gap={2}>
              <SectionCard title="Twoja wizyta" contentSx={{ pt: 0.5, pb: 1 }}>
                <SummaryRow label="Usługa" onChange={() => setActiveStep(0)}>
                  {selectedService.name}
                  <Typography variant="caption" component="div">{formatDuration(selectedService.duration)}</Typography>
                </SummaryRow>
                <SummaryRow label="Specjalista" onChange={() => setActiveStep(1)}>
                  {selectedStaff.firstName} {selectedStaff.lastName}
                </SummaryRow>
                <SummaryRow label="Termin" onChange={() => setActiveStep(2)}>
                  <Box component="span" sx={{ '&::first-letter': { textTransform: 'uppercase' }, display: 'inline-block' }}>{termLabel}</Box>
                </SummaryRow>
                <SummaryRow label="Cena" strong>{formatMoney(selectedService.price)}</SummaryRow>
              </SectionCard>

              <SectionCard title="Twoje dane" subtitle="Pola oznaczone gwiazdką są wymagane">
                <FieldGrid columns={2}>
                  <TextField
                    label="Imię"
                    value={guest.firstName}
                    onChange={(e) => setGuest((p) => ({ ...p, firstName: e.target.value }))}
                    fullWidth
                    required
                    autoComplete="given-name"
                  />
                  <TextField
                    label="Nazwisko"
                    value={guest.lastName}
                    onChange={(e) => setGuest((p) => ({ ...p, lastName: e.target.value }))}
                    fullWidth
                    required
                    autoComplete="family-name"
                  />
                  <TextField
                    label="E-mail"
                    type="email"
                    value={guest.email}
                    onChange={(e) => setGuest((p) => ({ ...p, email: e.target.value }))}
                    fullWidth
                    required
                    autoComplete="email"
                    inputProps={{ inputMode: 'email' }}
                  />
                  <TextField
                    label="Telefon"
                    type="tel"
                    value={guest.phone}
                    onChange={(e) => setGuest((p) => ({ ...p, phone: e.target.value }))}
                    fullWidth
                    autoComplete="tel"
                    helperText="Ułatwi salonowi kontakt w razie zmiany terminu"
                  />
                </FieldGrid>
                <TextField
                  label="Uwagi dla salonu"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  helperText="Opcjonalnie, np. preferencje dotyczące wizyty"
                  sx={{ mt: 2 }}
                />
              </SectionCard>
            </Stack>

            {error && (
              <Alert severity="error" role="alert" sx={{ mt: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <ActionBar>
              <Button variant="text" onClick={() => setActiveStep(2)} disabled={submitting} sx={{ ml: { sm: -1.5 } }}>
                Wstecz
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={handleBook}
                sx={{ flex: { xs: 1, sm: 'none' }, minWidth: { sm: 240 } }}
                disabled={
                  submitting ||
                  !guest.firstName.trim() ||
                  !guest.lastName.trim() ||
                  !guest.email.trim()
                }
              >
                {submitting ? 'Rezerwowanie…' : 'Potwierdź rezerwację'}
              </Button>
            </ActionBar>
          </Box>
        )}
      </Container>
    </Box>
  );
};
